import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@chakra-ui/react";
import { FaAngleLeft } from "react-icons/fa";
import axios from "axios";
import SweetAlert2 from "react-sweetalert2";
import Card from "components/card";
import InputField from "components/fields/InputField";
import { getFeeTypeDisplayName } from "../utils/feeTypeDisplayNames";

const FIXED_TYPES = ["payout", "withdrawal"];

const filterNumericInput = (value: string): string => {
  if (value === "") return "";
  let filtered = value.replace(/[^0-9.]/g, "");
  const parts = filtered.split(".");
  if (parts.length > 2) {
    filtered = parts[0] + "." + parts.slice(1).join("");
  }
  return filtered;
};

type FeeData = {
  type: string;
  asset: string;
  asset_type: string;
  display_name: string;
  decimal: number;
  fixed: string;
  percentage: string;
  max_amount: string;
  min_amount: string;
  is_crypto: boolean;
};

const generateDecimalPlaceholder = (decimal: number): string => {
  if (decimal <= 0) return "0";
  return "0." + "0".repeat(decimal);
};

const CreateFeeGroupSubaccount = ({
  userPermissions,
}: {
  userPermissions: any;
}) => {
  const navigate = useNavigate();
  const userRole = localStorage.getItem("userRole")?.toLowerCase() || "";

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isCreating, setIsCreating] = useState<boolean>(false);
  const [name, setName] = useState<string>("");
  const [description, setDescription] = useState<string>("");
  const [swalProps, setSwalProps] = useState<any>({});
  const [feeData, setFeeData] = useState<FeeData[]>([]);
  const [openSections, setOpenSections] = useState<{ [key: string]: boolean }>(
    FIXED_TYPES.reduce(
      (acc, type, index) => ({ ...acc, [type]: index === 0 }),
      {}
    )
  );

  const areAllOpen = Object.values(openSections).every(Boolean);

  const toggleAllSections = () => {
    const newState = !areAllOpen;
    setOpenSections(
      Object.keys(openSections).reduce(
        (acc, key) => ({ ...acc, [key]: newState }),
        {}
      )
    );
  };

  useEffect(() => {
    fetchAssetWhitelist();
  }, []);

  const fetchAssetWhitelist = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(
        process.env.REACT_APP_API_URL + `/api/feegroup/all-assets`
      );

      const assetList = response.data?.assets?.payload || [];

      // Initialize fee data for all combinations of assets and types (only fiat for payout/withdrawal)
      const data: FeeData[] = [];
      FIXED_TYPES.forEach((type) => {
        assetList.forEach((a: any) => {
          const assetDecimal = a.decimal ?? 2;
          const baseDisplayName = a.display_name || a.code || a.asset;
          if (a.crypto === false) {
            data.push({
              type,
              asset: a.code || a.asset,
              asset_type: "fiat",
              display_name: baseDisplayName,
              decimal: assetDecimal,
              fixed: generateDecimalPlaceholder(assetDecimal),
              percentage: "0",
              max_amount: generateDecimalPlaceholder(assetDecimal),
              min_amount: generateDecimalPlaceholder(assetDecimal),
              is_crypto: false,
            });
          } else {
            if (a.networks.length !== 0) {
              const hasMultipleNetworks = a.networks.length > 1;
              a.networks.forEach((n: any) => {
                // For crypto with multiple networks, show "Asset-Network"
                const cryptoDisplayName = hasMultipleNetworks
                  ? `${baseDisplayName}-${n.display_name || n.network}`
                  : baseDisplayName;
                data.push({
                  type,
                  asset: a.code || a.asset,
                  asset_type: n.network.toLowerCase(),
                  display_name: cryptoDisplayName,
                  decimal: assetDecimal,
                  fixed: generateDecimalPlaceholder(assetDecimal),
                  percentage: "0",
                  max_amount: generateDecimalPlaceholder(assetDecimal),
                  min_amount: generateDecimalPlaceholder(assetDecimal),
                  is_crypto: true,
                });
              });
            }
          }
        });
      });
      setFeeData(data);
    } catch (error: any) {
      console.error("Error fetching asset whitelist:", error);
      setSwalProps({
        show: true,
        icon: "error",
        title: "Oops!",
        html: "Failed to load asset whitelist",
        showConfirmButton: true,
        didClose: () => setSwalProps({}),
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleFeeDataChange = (index: number, field: string, value: string) => {
    setFeeData((prev) =>
      prev.map((item, i) => (i === index ? { ...item, [field]: value } : item))
    );
  };

  const handleCreate = async () => {
    if (!name || !description) {
      setSwalProps({
        show: true,
        icon: "warning",
        title: "Required Fields",
        html: "Please fill in name and description",
        showConfirmButton: true,
        didClose: () => setSwalProps({}),
      });
      return;
    }

    setIsCreating(true);

    try {
      const payload = {
        name,
        description,
        fee_data: feeData.map((item) => ({
          type: item.type,
          asset: item.asset,
          asset_type: item.asset_type,
          fixed: parseFloat(item.fixed) || 0,
          percentage: parseFloat(item.percentage) || 0,
          max_amount: parseFloat(item.max_amount) || 0,
          min_amount: parseFloat(item.min_amount) || 0,
        })),
        __with_default_feetemplate: true,
        username: localStorage.getItem("username"),
        userRole: userRole,
      };

      const response = await axios.post(
        process.env.REACT_APP_API_URL +
          `/api/${userRole}/feegroup/action/create-feegroup`,
        payload
      );

      if (response && response.data?.status === "ok") {
        setSwalProps({
          show: true,
          icon: "success",
          title: "Success",
          html: "Fee group created successfully",
          showConfirmButton: true,
          didClose: () => {
            setSwalProps({});
            // Navigate to detail page if we get the ID back, otherwise go to list
            const newId = response.data?.result?.payload?.feegroup_id;
            if (newId) {
              navigate(`/${userRole}/fee-groups/subaccount/${newId}`);
            } else {
              navigate(`/${userRole}/fee-groups/subaccount`);
            }
          },
        });
      } else {
        setSwalProps({
          show: true,
          icon: "error",
          title: "Oops!",
          html: response.data?.message || "Failed to create fee group",
          showConfirmButton: true,
          didClose: () => setSwalProps({}),
        });
      }
    } catch (error: any) {
      console.error("Error creating fee group:", error);
      setSwalProps({
        show: true,
        icon: "error",
        title: "Oops!",
        html:
          error.response?.data?.message ||
          "Something went wrong, please try again later.",
        showConfirmButton: true,
        didClose: () => setSwalProps({}),
      });
    } finally {
      setIsCreating(false);
    }
  };

  const isFormValid = name && description;

  if (isLoading) {
    return (
      <>
        {/* Header skeleton */}
        <Card className="card !rounded-xl">
          <div className="flex items-center p-8 py-6">
            <div
              className="h-7 w-48 animate-pulse rounded bg-gray-200"
              style={{ animationDuration: "0.6s" }}
            />
          </div>

          <div className="border-b border-gray-200 px-6 pb-6">
            <div
              className="mb-4 h-6 w-48 animate-pulse rounded bg-gray-200"
              style={{ animationDuration: "0.6s" }}
            />
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div>
                <div
                  className="mb-2 h-4 w-16 animate-pulse rounded bg-gray-200"
                  style={{ animationDuration: "0.6s" }}
                />
                <div
                  className="h-10 w-full animate-pulse rounded-md bg-gray-200"
                  style={{ animationDuration: "0.6s" }}
                />
              </div>
              <div>
                <div
                  className="mb-2 h-4 w-24 animate-pulse rounded bg-gray-200"
                  style={{ animationDuration: "0.6s" }}
                />
                <div
                  className="h-10 w-full animate-pulse rounded-md bg-gray-200"
                  style={{ animationDuration: "0.6s" }}
                />
              </div>
            </div>
          </div>

          {/* Fee Configuration skeleton */}
          <div className="mt-6">
            {[1, 2].map((i) => (
              <div key={i} className="mb-4 px-6 pb-6">
                <div
                  className="mb-4 h-5 w-32 animate-pulse rounded bg-gray-200"
                  style={{ animationDuration: "0.6s" }}
                />
                <div className="space-y-4">
                  {[1, 2, 3].map((j) => (
                    <div key={j} className="grid grid-cols-6 gap-4">
                      <div
                        className="h-10 animate-pulse rounded-md bg-gray-200"
                        style={{ animationDuration: "0.6s" }}
                      />
                      <div
                        className="h-10 animate-pulse rounded-md bg-gray-200"
                        style={{ animationDuration: "0.6s" }}
                      />
                      <div
                        className="h-10 animate-pulse rounded-md bg-gray-200"
                        style={{ animationDuration: "0.6s" }}
                      />
                      <div
                        className="h-10 animate-pulse rounded-md bg-gray-200"
                        style={{ animationDuration: "0.6s" }}
                      />
                      <div
                        className="h-10 animate-pulse rounded-md bg-gray-200"
                        style={{ animationDuration: "0.6s" }}
                      />
                      <div
                        className="h-10 animate-pulse rounded-md bg-gray-200"
                        style={{ animationDuration: "0.6s" }}
                      />
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {/* Create button skeleton */}
          <div className="mt-6 flex justify-end">
            <div
              className="h-10 w-36 animate-pulse rounded-md bg-gray-200"
              style={{ animationDuration: "0.6s" }}
            />
          </div>
        </Card>
      </>
    );
  }

  return (
    <div className="pb-6">
      <Card className="card card-pad !rounded-xl">
        <div className="mb-3 flex items-center">
          <div>
            <button
              type="button"
              onClick={() => navigate(`/${userRole}/fee-groups/subaccount`)}
              disabled={isCreating}
              className="
                mb-1 inline-flex items-center gap-1
                text-sm font-medium text-gray-700
                transition hover:text-gray-700
                disabled:cursor-not-allowed disabled:opacity-40
                dark:text-gray-400 dark:hover:text-white
              "
            >
              <FaAngleLeft className="text-base" />
              <span>Subaccount</span>
            </button>

            <h3 className="text-xl font-semibold text-navy-700">
              Create New Fee Group
            </h3>
          </div>
        </div>

        <div className="my-4 text-sm text-navy-700 dark:text-white">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <InputField
                id="name"
                label="Name"
                placeholder="Enter fee group name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                disabled={isCreating}
                variant="auth"
                extra="w-full"
                type="text"
                required
              />
            </div>
            <div>
              <InputField
                id="description"
                label="Description"
                placeholder="Enter description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                disabled={isCreating}
                variant="auth"
                extra="w-full"
                type="text"
                required
              />
            </div>
          </div>
        </div>

        <hr className="my-10" />
        <div>
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <h3 className="text-lg font-medium text-navy-700">
                Fee Configuration
              </h3>
              <p className="mt-1 text-sm text-gray-500 dark:text-white/60">
                Manage templates by fee type. Expand sections to review and
                edit.
              </p>
            </div>

            <button
              type="button"
              onClick={toggleAllSections}
              className="dark:bg-neutral-900 shrink-0 rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-navy-700 shadow-sm transition hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500/30 dark:border-white/10 dark:text-white dark:hover:bg-white/5"
            >
              {areAllOpen ? (
                <span className="flex items-center gap-2">
                  <span className="inline-block h-2 w-2 rounded-full bg-gray-400" />
                  Collapse all
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <span className="inline-block h-2 w-2 rounded-full bg-primary-500" />
                  Show all
                </span>
              )}
            </button>
          </div>

          {FIXED_TYPES.map((type) => {
            const typeFees = feeData.filter((f) => f.type === type);
            const fiatFees = typeFees.filter((f) => !f.is_crypto);
            const cryptoFees = typeFees.filter((f) => f.is_crypto);
            return (
              <div
                key={type}
                className="card my-4 mb-4 !rounded-xl border border-gray-200 p-6"
              >
                <button
                  className="flex w-full items-center justify-between text-base font-semibold capitalize text-navy-700"
                  onClick={() =>
                    setOpenSections((prev) => ({
                      ...prev,
                      [type]: !prev[type],
                    }))
                  }
                >
                  <span>{getFeeTypeDisplayName(type)}</span>
                  <svg
                    className={`h-4 w-4 transform transition-transform duration-200 ${
                      openSections[type] ? "rotate-180" : ""
                    }`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </button>
                {openSections[type] && (
                  <div className="mt-4">
                    {fiatFees.length > 0 && (
                      <div className="mb-6">
                        <h4 className="mb-2 text-sm font-semibold text-navy-700">
                          Fiat
                        </h4>
                        <div className="overflow-x-auto">
                          <div className="w-full">
                            <div
                              className="MuiDataGrid-columnHeaders"
                              style={{
                                minHeight: 48,
                                maxHeight: 48,
                                lineHeight: "48px",
                              }}
                            >
                              <div
                                className="MuiDataGrid-columnHeaderRow flex"
                                style={{ height: 48 }}
                              >
                                <div
                                  className="MuiDataGrid-columnHeader flex items-center"
                                  style={{
                                    width: "28%",
                                    minWidth: "28%",
                                    padding: "8px 16px",
                                    borderTopLeftRadius: 5,
                                  }}
                                >
                                  <div className="MuiDataGrid-columnHeaderTitle">
                                    Asset
                                  </div>
                                </div>
                                <div
                                  className="MuiDataGrid-columnHeader flex items-center"
                                  style={{
                                    width: "18%",
                                    minWidth: "18%",
                                    padding: "8px 16px",
                                  }}
                                >
                                  <div className="MuiDataGrid-columnHeaderTitle">
                                    Min
                                  </div>
                                </div>
                                <div
                                  className="MuiDataGrid-columnHeader flex items-center"
                                  style={{
                                    width: "18%",
                                    minWidth: "18%",
                                    padding: "8px 16px",
                                  }}
                                >
                                  <div className="MuiDataGrid-columnHeaderTitle">
                                    Max
                                  </div>
                                </div>
                                <div
                                  className="MuiDataGrid-columnHeader flex items-center"
                                  style={{
                                    width: "18%",
                                    minWidth: "18%",
                                    padding: "8px 16px",
                                  }}
                                >
                                  <div className="MuiDataGrid-columnHeaderTitle">
                                    Percentage
                                  </div>
                                </div>
                                <div
                                  className="MuiDataGrid-columnHeader flex items-center"
                                  style={{
                                    width: "18%",
                                    minWidth: "18%",
                                    padding: "8px 16px",
                                    borderTopRightRadius: 5,
                                  }}
                                >
                                  <div className="MuiDataGrid-columnHeaderTitle">
                                    Fixed
                                  </div>
                                </div>
                              </div>
                            </div>
                            {fiatFees.map((fee) => {
                              const globalIndex = feeData.indexOf(fee);
                              return (
                                <div
                                  key={globalIndex}
                                  className="MuiDataGrid-row group flex"
                                  style={{
                                    minHeight: 48,
                                    borderBottom:
                                      "1px solid rgba(224, 224, 224, 1)",
                                  }}
                                >
                                  <div
                                    className="MuiDataGrid-cell flex items-center"
                                    style={{ width: "28%" }}
                                  >
                                    {fee.display_name}
                                  </div>
                                  <div
                                    className="MuiDataGrid-cell flex items-center"
                                    style={{ width: "18%" }}
                                  >
                                    <InputField
                                      id={`min-${globalIndex}`}
                                      label=""
                                      placeholder={generateDecimalPlaceholder(
                                        fee.decimal
                                      )}
                                      value={fee.min_amount}
                                      onChange={(e) =>
                                        handleFeeDataChange(
                                          globalIndex,
                                          "min_amount",
                                          filterNumericInput(e.target.value)
                                        )
                                      }
                                      disabled={isCreating}
                                      variant="auth"
                                      extra="w-full"
                                      type="text"
                                      hideLabel
                                    />
                                  </div>
                                  <div
                                    className="MuiDataGrid-cell flex items-center"
                                    style={{ width: "18%" }}
                                  >
                                    <InputField
                                      id={`max-${globalIndex}`}
                                      label=""
                                      placeholder={generateDecimalPlaceholder(
                                        fee.decimal
                                      )}
                                      value={fee.max_amount}
                                      onChange={(e) =>
                                        handleFeeDataChange(
                                          globalIndex,
                                          "max_amount",
                                          filterNumericInput(e.target.value)
                                        )
                                      }
                                      disabled={isCreating}
                                      variant="auth"
                                      extra="w-full"
                                      type="text"
                                      hideLabel
                                    />
                                  </div>
                                  <div
                                    className="MuiDataGrid-cell flex items-center"
                                    style={{ width: "18%" }}
                                  >
                                    <InputField
                                      id={`percentage-${globalIndex}`}
                                      label=""
                                      placeholder="0"
                                      value={fee.percentage}
                                      onChange={(e) =>
                                        handleFeeDataChange(
                                          globalIndex,
                                          "percentage",
                                          filterNumericInput(e.target.value)
                                        )
                                      }
                                      disabled={isCreating}
                                      variant="auth"
                                      extra="w-full"
                                      type="text"
                                      hideLabel
                                    />
                                  </div>
                                  <div
                                    className="MuiDataGrid-cell flex items-center"
                                    style={{ width: "18%" }}
                                  >
                                    <InputField
                                      id={`fixed-${globalIndex}`}
                                      label=""
                                      placeholder={generateDecimalPlaceholder(
                                        fee.decimal
                                      )}
                                      value={fee.fixed}
                                      onChange={(e) =>
                                        handleFeeDataChange(
                                          globalIndex,
                                          "fixed",
                                          filterNumericInput(e.target.value)
                                        )
                                      }
                                      disabled={isCreating}
                                      variant="auth"
                                      extra="w-full"
                                      type="text"
                                      hideLabel
                                    />
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      </div>
                    )}

                    {cryptoFees.length > 0 && (
                      <div>
                        <h4 className="mb-2 text-sm font-semibold text-navy-700">
                          Crypto
                        </h4>
                        <div className="overflow-x-auto">
                          <div className="w-full">
                            <div
                              className="MuiDataGrid-columnHeaders"
                              style={{
                                minHeight: 48,
                                maxHeight: 48,
                                lineHeight: "48px",
                              }}
                            >
                              <div
                                className="MuiDataGrid-columnHeaderRow flex"
                                style={{ height: 48 }}
                              >
                                <div
                                  className="MuiDataGrid-columnHeader flex items-center"
                                  style={{
                                    width: "20%",
                                    minWidth: "20%",
                                    padding: "8px 16px",
                                    borderTopLeftRadius: 5,
                                  }}
                                >
                                  <div className="MuiDataGrid-columnHeaderTitle">
                                    Asset
                                  </div>
                                </div>
                                <div
                                  className="MuiDataGrid-columnHeader flex items-center"
                                  style={{
                                    width: "16%",
                                    minWidth: "16%",
                                    padding: "8px 16px",
                                  }}
                                >
                                  <div className="MuiDataGrid-columnHeaderTitle">
                                    Asset Type
                                  </div>
                                </div>
                                <div
                                  className="MuiDataGrid-columnHeader flex items-center"
                                  style={{
                                    width: "16%",
                                    minWidth: "16%",
                                    padding: "8px 16px",
                                  }}
                                >
                                  <div className="MuiDataGrid-columnHeaderTitle">
                                    Min
                                  </div>
                                </div>
                                <div
                                  className="MuiDataGrid-columnHeader flex items-center"
                                  style={{
                                    width: "16%",
                                    minWidth: "16%",
                                    padding: "8px 16px",
                                  }}
                                >
                                  <div className="MuiDataGrid-columnHeaderTitle">
                                    Max
                                  </div>
                                </div>
                                <div
                                  className="MuiDataGrid-columnHeader flex items-center"
                                  style={{
                                    width: "16%",
                                    minWidth: "16%",
                                    padding: "8px 16px",
                                  }}
                                >
                                  <div className="MuiDataGrid-columnHeaderTitle">
                                    Percentage
                                  </div>
                                </div>
                                <div
                                  className="MuiDataGrid-columnHeader flex items-center"
                                  style={{
                                    width: "16%",
                                    minWidth: "16%",
                                    padding: "8px 16px",
                                    borderTopRightRadius: 5,
                                  }}
                                >
                                  <div className="MuiDataGrid-columnHeaderTitle">
                                    Fixed
                                  </div>
                                </div>
                              </div>
                            </div>
                            {cryptoFees.map((fee) => {
                              const globalIndex = feeData.indexOf(fee);
                              return (
                                <div
                                  key={globalIndex}
                                  className="MuiDataGrid-row group flex"
                                  style={{
                                    minHeight: 48,
                                    borderBottom:
                                      "1px solid rgba(224, 224, 224, 1)",
                                  }}
                                >
                                  <div
                                    className="MuiDataGrid-cell flex items-center"
                                    style={{ width: "20%" }}
                                  >
                                    {fee.display_name}
                                  </div>
                                  <div
                                    className="MuiDataGrid-cell flex items-center"
                                    style={{ width: "16%" }}
                                  >
                                    {fee.asset_type}
                                  </div>
                                  <div
                                    className="MuiDataGrid-cell flex items-center"
                                    style={{ width: "16%" }}
                                  >
                                    <InputField
                                      id={`min-${globalIndex}`}
                                      label=""
                                      placeholder={generateDecimalPlaceholder(
                                        fee.decimal
                                      )}
                                      value={fee.min_amount}
                                      onChange={(e) =>
                                        handleFeeDataChange(
                                          globalIndex,
                                          "min_amount",
                                          filterNumericInput(e.target.value)
                                        )
                                      }
                                      disabled={isCreating}
                                      variant="auth"
                                      extra="w-full"
                                      type="text"
                                      hideLabel
                                    />
                                  </div>
                                  <div
                                    className="MuiDataGrid-cell flex items-center"
                                    style={{ width: "16%" }}
                                  >
                                    <InputField
                                      id={`max-${globalIndex}`}
                                      label=""
                                      placeholder={generateDecimalPlaceholder(
                                        fee.decimal
                                      )}
                                      value={fee.max_amount}
                                      onChange={(e) =>
                                        handleFeeDataChange(
                                          globalIndex,
                                          "max_amount",
                                          filterNumericInput(e.target.value)
                                        )
                                      }
                                      disabled={isCreating}
                                      variant="auth"
                                      extra="w-full"
                                      type="text"
                                      hideLabel
                                    />
                                  </div>
                                  <div
                                    className="MuiDataGrid-cell flex items-center"
                                    style={{ width: "16%" }}
                                  >
                                    <InputField
                                      id={`percentage-${globalIndex}`}
                                      label=""
                                      placeholder="0"
                                      value={fee.percentage}
                                      onChange={(e) =>
                                        handleFeeDataChange(
                                          globalIndex,
                                          "percentage",
                                          filterNumericInput(e.target.value)
                                        )
                                      }
                                      disabled={isCreating}
                                      variant="auth"
                                      extra="w-full"
                                      type="text"
                                      hideLabel
                                    />
                                  </div>
                                  <div
                                    className="MuiDataGrid-cell flex items-center"
                                    style={{ width: "16%" }}
                                  >
                                    <InputField
                                      id={`fixed-${globalIndex}`}
                                      label=""
                                      placeholder={generateDecimalPlaceholder(
                                        fee.decimal
                                      )}
                                      value={fee.fixed}
                                      onChange={(e) =>
                                        handleFeeDataChange(
                                          globalIndex,
                                          "fixed",
                                          filterNumericInput(e.target.value)
                                        )
                                      }
                                      disabled={isCreating}
                                      variant="auth"
                                      extra="w-full"
                                      type="text"
                                      hideLabel
                                    />
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div className="mb-4 mt-6 flex justify-end">
          <Button
            onClick={handleCreate}
            isDisabled={!isFormValid || isCreating}
            isLoading={isCreating}
            loadingText="Creating..."
            className="btn-primary relative overflow-hidden rounded-md px-8 py-2.5 font-semibold text-white shadow-md"
          >
            <span className="relative z-10 tracking-tight">
              Create Fee Group
            </span>
          </Button>
        </div>
      </Card>

      <SweetAlert2
        {...swalProps}
        customClass={{
          actions: "custom-swal2-popup-action",
          title: "custom-swal2-popup-title",
          confirmButton:
            "custom-swal2-popup-action custom-swal2-popup-action-confirm",
          cancelButton:
            "custom-swal2-popup-action custom-swal2-popup-action-cancel",
        }}
      />
    </div>
  );
};

export default CreateFeeGroupSubaccount;
